import { useId } from 'react';
import { MONSTER_TOUGHNESS_PATH, MONSTER_COMBAT_DAMAGE_PATH } from './MonsterStatIcon';
import { MonsterInfoButton } from './MonsterInspector';
import { Monster } from '../api';

const MOVEMENT_BORDER_COLORS: Record<Monster['movementType'], string> = {
  Normal: '#000000',
  Fast: '#ff0000',
  Special: '#00ff00',
  Stationary: '#ffff00',
  Flying: '#0000ff',
  Stalker: '#a855f7',
  Aquatic: '#f97316',
};

export const getMonsterMovementBorderColor = (movementType: Monster['movementType']): string =>
  MOVEMENT_BORDER_COLORS[movementType] ?? MOVEMENT_BORDER_COLORS.Normal;

export const getMonsterEffectiveMovementType = (monster: Monster): Monster['movementType'] =>
  (monster.effectiveMovementType ?? monster.movementType) || '';

const formatSignedRating = (rating: number): string => rating >= 0 ? `+${rating}` : `${rating}`;
const MONSTER_REWORK_ASSET_ALIASES: Record<string, string> = {
  DimensionalShambler: 'DimensionalShampler',
};

const normalizeMonsterAssetName = (monsterName: string): string => monsterName.replace(/\s+/g, '');

export const getMonsterReworkImagePath = (monsterName: string): string =>
  `/Monsters/rework/${MONSTER_REWORK_ASSET_ALIASES[normalizeMonsterAssetName(monsterName)] ?? normalizeMonsterAssetName(monsterName)}.jpg`;

const splitMonsterNameLines = (monsterName: string): string[] => {
  const normalizedName = monsterName.trim().replace(/\s+/g, ' ');
  if (normalizedName.length <= 18 || !normalizedName.includes(' ')) {
    return [normalizedName];
  }

  const words = normalizedName.split(' ');
  let bestLines: string[] = [normalizedName];
  let bestScore = Number.MAX_SAFE_INTEGER;

  for (let splitIndex = 1; splitIndex < words.length; splitIndex += 1) {
    const lines = [
      words.slice(0, splitIndex).join(' '),
      words.slice(splitIndex).join(' '),
    ];
    const longestLineLength = Math.max(...lines.map((line) => line.length));
    const balancePenalty = Math.abs(lines[0].length - lines[1].length);
    const score = longestLineLength * 10 + balancePenalty;

    if (score < bestScore) {
      bestLines = lines;
      bestScore = score;
    }
  }

  return bestLines;
};

const getMonsterNameLabelWidth = (nameLines: string[]): number =>
  Math.min(128, Math.max(24, Math.max(...nameLines.map((line) => line.length)) * 7 + 18));

const getBreakdownTotal = (
  breakdown: { total?: number } | undefined,
  fallback: number,
): number => typeof breakdown?.total === 'number' ? breakdown.total : fallback;

const getMonsterToughness = (monster: Monster): number =>
  getBreakdownTotal(monster.toughnessBreakdown, monster.Toughness ?? monster.toughness ?? 1);

const getMonsterAwareness = (monster: Monster): number =>
  getBreakdownTotal(monster.awarenessBreakdown, monster.awareness);

const getMonsterCombatRating = (monster: Monster): number =>
  getBreakdownTotal(monster.combatRatingBreakdown, monster.combatRating);

const hasSpecialCombatRating = (monster: Monster): boolean =>
  Boolean(monster.specialCombatRating ?? monster.SpecialCombatRating);

const getMonsterCombatDamage = (monster: Monster): number =>
  getBreakdownTotal(monster.combatDamageBreakdown, monster.combatDamage);

const hasSpecialCombatDamage = (monster: Monster): boolean =>
  Boolean(monster.specialCombatDamage ?? monster.SpecialCombatDamage);

const getMonsterHorrorRating = (monster: Monster): number =>
  getBreakdownTotal(monster.horrorRatingBreakdown, monster.horrorRating);

const getMonsterHorrorDamage = (monster: Monster): number =>
  getBreakdownTotal(monster.horrorDamageBreakdown, monster.horrorDamage);

const hasSpecialHorrorDamage = (monster: Monster): boolean =>
  Boolean(monster.specialHorrorDamage ?? monster.SpecialHorrorDamage);

const getMonsterAbilities = (monster: Monster): string[] =>
  monster.abilities ?? monster.Abilities ?? [];

const hasMonsterAbility = (monster: Monster, abilityName: string): boolean =>
  getMonsterAbilities(monster).some(
    (ability) => ability.trim().toLowerCase() === abilityName.toLowerCase(),
  );

const getMonsterAdditionalInfo = (monster: Monster): string =>
  monster.additionalInfo ?? monster.AdditionalInfo ?? '';

interface MonsterTokenSvgProps {
  monster: Monster;
  stopPropagation?: boolean;
  showInfo?: boolean;
}

const getStackedToughnessDropPositions = (toughness: number): { x: number; y: number }[] => {
  const visibleDrops = Math.max(1, Math.min(5, toughness));
  const horizontalStep = 8;
  const startX = 100 - ((visibleDrops - 1) * horizontalStep) / 2;

  return Array.from({ length: visibleDrops }, (_, index) => ({
    x: startX + index * horizontalStep,
    y: 158 + index * 4,
  }));
};

const renderBloodDrop = (key: string, x: number, y: number, scale = 0.5) => (
  <g key={key} transform={`translate(${x} ${y}) scale(${scale})`}>
    <path
      data-testid="monster-token-toughness-drop"
      d={MONSTER_TOUGHNESS_PATH}
      fill="#b9151b"
      stroke="#ffffff"
      strokeWidth="5"
    />
  </g>
);

export const MonsterTokenBack: React.FC<MonsterTokenSvgProps> = ({ monster, stopPropagation }) => {
  const abilities = getMonsterAbilities(monster);
  const suppressedAbilities = monster.suppressedAbilities ?? monster.SuppressedAbilities ?? [];
  const hasAbilities = abilities.length > 0 || suppressedAbilities.length > 0;
  const additionalInfoLines = getMonsterAdditionalInfo(monster)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div
      className="monster-token-back"
      data-testid="monster-token-back"
      role="img"
      aria-label={`${monster.name} monster back`}
      onClick={(e) => { if (stopPropagation) { e.stopPropagation(); } }}
    >
      <MonsterTokenSvg monster={monster} />
      {(hasAbilities || additionalInfoLines.length > 0) && (
        <div className="monster-token-back-text">
          {hasAbilities && (
            <div className="monster-token-abilities" data-testid="monster-token-abilities">
              {abilities.map((ability) => (
                <div key={`active-${ability}`} className="monster-token-ability">
                  <strong>{ability}</strong>
                </div>
              ))}
              {suppressedAbilities.map((ability) => (
                <div key={`suppressed-${ability}`} className="monster-token-ability monster-token-ability-suppressed">
                  <strong>{ability}</strong>
                </div>
              ))}
            </div>
          )}
          {additionalInfoLines.length > 0 && (
            <div className={`monster-token-additional-info ${monster.name}`} data-testid="monster-token-additional-info">
              {additionalInfoLines.map((line) => (
                <div key={line} className="monster-token-additional-info-line">
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const MonsterTokenFrontSvg: React.FC<MonsterTokenSvgProps> = ({ monster, stopPropagation, showInfo = true }) => {
  const artworkClipId = `monster-front-art-${useId().replace(/:/g, '')}`;
  const borderColor = getMonsterMovementBorderColor(getMonsterEffectiveMovementType(monster));
  const awarenessRating = formatSignedRating(getMonsterAwareness(monster));
  const awarenessColor = hasMonsterAbility(monster, 'Elusive') ? '#16a34a' : '#b9151b';
  const nameLines = splitMonsterNameLines(monster.name);
  const nameLabelWidth = getMonsterNameLabelWidth(nameLines);
  const nameLabelHeight = nameLines.length > 1 ? 34 : 20;
  const nameFontSize = Math.min(12, 110 / (Math.max(...nameLines.map(line => line.length)) * .65));

  return (
    <div
      className="monster-token-front"
      data-testid="monster-token-front-svg"
      role="img"
      aria-label={`${monster.name} monster front`}
      onClick={(e) => { if (stopPropagation) { e.stopPropagation(); } }}
    >
      {showInfo && <MonsterInfoButton monster={monster} />}
      <svg
        className="monster-token-svg"
        width="200"
        height="200"
        viewBox="0 0 200 200"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="4" y="4" width="192" height="192" rx="14" fill="#eee8dc" stroke={borderColor} strokeWidth="8" />
        <defs>
          <clipPath id={artworkClipId}><rect x="8" y="8" width="184" height="184" rx="10" /></clipPath>
        </defs>
        <image
          data-testid="monster-token-front-art"
          href={getMonsterReworkImagePath(monster.name)}
          x="8"
          y="8"
          width="184"
          height="184"
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${artworkClipId})`}
        />

        <g data-testid="monster-token-front-name">
          <rect
            className="monster-token-front-badge"
            x="12"
            y="12"
            width={nameLabelWidth}
            height={nameLabelHeight}
            rx="6"
          />
          <text
            x="18"
            y={nameLines.length > 1 ? 25 : 26}
            fontFamily="system-ui, sans-serif"
            fontSize={nameFontSize}
            fontWeight="700"
            fill="#111111"
          >
            {nameLines.map((line, index) => (
              <tspan key={`${line}-${index}`} x="18" dy={index === 0 ? 0 : 13}>
                {line}
              </tspan>
            ))}
          </text>
        </g>

        <rect className="monster-token-front-badge" x="148" y="12" width="40" height="40" rx="10" />
        <text
          data-testid="monster-token-front-awareness"
          x="168"
          y="40"
          textAnchor="middle"
          fontFamily="system-ui, sans-serif"
          fontSize={awarenessRating.length > 2 ? 22 : 28}
          fontWeight="700"
          fill={awarenessColor}
        >
          {awarenessRating}
        </text>

        {monster.type === 'Spawn' && (
          <g data-testid="monster-token-spawn-indicator">
            <rect
              data-testid="monster-token-spawn-background"
              className="monster-token-front-badge"
              x="12" y="148" width="40" height="40" rx="10"
            />
            <circle cx="32" cy="168" r="11" fill="#d61018" stroke="#ffffff" strokeWidth="2" />
          </g>
        )}

        <g data-testid="monster-token-dimension-sign">
          <rect
            data-testid="monster-token-dimension-background"
            className="monster-token-front-badge"
            x="148" y="148" width="40" height="40" rx="10"
          />
          <image
            href={`/signs/${monster.dimensionSign}.svg`}
            x="154"
            y="154"
            width="28"
            height="28"
            preserveAspectRatio="xMidYMid meet"
          />
        </g>
      </svg>
    </div>
  );
};

export const MonsterTokenSvg: React.FC<MonsterTokenSvgProps> = ({ monster }) => {
  const borderColor = getMonsterMovementBorderColor(getMonsterEffectiveMovementType(monster));
  const horrorDamage = getMonsterHorrorDamage(monster);
  const specialHorrorDamage = hasSpecialHorrorDamage(monster);
  const horrorRating = formatSignedRating(getMonsterHorrorRating(monster));
  const combatDamage = getMonsterCombatDamage(monster);
  const specialCombatDamage = hasSpecialCombatDamage(monster);
  const combatRating = hasSpecialCombatRating(monster)
    ? 'S'
    : formatSignedRating(getMonsterCombatRating(monster));
  const grayscaleFilterId = `monster-token-back-grayscale-${normalizeMonsterAssetName(monster.name).replace(/[^a-z0-9_-]/gi, '')}`;
  const toughness = getMonsterToughness(monster);
  const stackedToughnessDrops = toughness <= 5
    ? getStackedToughnessDropPositions(toughness)
    : [];

  return (
    <svg
      className="monster-token-svg"
      data-testid="monster-token-svg"
      width="200"
      height="200"
      viewBox="0 0 200 200"
      role="img"
      aria-label={`${monster.name} monster stats`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{`${monster.name} monster stats`}</title>
      <defs>
        <filter id={grayscaleFilterId}>
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>
      <rect x="8" y="8" width="184" height="184" fill="#f0f0f0" />
      <image
        data-testid="monster-token-back-art"
        href={getMonsterReworkImagePath(monster.name)}
        x="8"
        y="8"
        width="184"
        height="184"
        preserveAspectRatio="xMidYMid slice"
        opacity="0.22"
        filter={`url(#${grayscaleFilterId})`}
      />
      <rect
        data-testid="monster-token-background"
        x="4"
        y="4"
        width="192"
        height="192"
        fill="none"
        stroke={borderColor}
        strokeWidth="8"
      />

      {(horrorDamage !== 0 || specialHorrorDamage) && (
        <text
          x="44"
          y="148"
          textAnchor="middle"
          fontFamily="Cinzel, Georgia, serif"
          fontSize="24"
          fontWeight="700"
          fill="#ffffff"
          stroke="#232323"
          strokeWidth="3"
          paintOrder="stroke"
        >
          {horrorRating}
        </text>
      )}

      {horrorDamage === 0 && !specialHorrorDamage ? (
        <line
          data-testid="monster-token-horror-minus"
          x1="27"
          y1="170"
          x2="61"
          y2="170"
          stroke="#1258c9"
          strokeWidth="10"
          strokeLinecap="round"
        />
      ) : (
        <g data-testid="monster-token-horror-damage">
          <ellipse
            cx="44"
            cy="170"
            rx="20"
            ry="15"
            fill="#1258c9"
            stroke="#ffffff"
            strokeWidth="5"
          />
          <text
            x="44"
            y="178"
            textAnchor="middle"
            fontFamily="Cinzel, Georgia, serif"
            fontSize="24"
            fontWeight="700"
            fill="#ffffff"
          >
            {specialHorrorDamage ? 'S' : horrorDamage}
          </text>
        </g>
      )}

      <g data-testid="monster-token-toughness">
        {stackedToughnessDrops.map((position, index) =>
          renderBloodDrop(`toughness-${index}`, position.x, position.y)
        )}
        {toughness >= 6 && (
          <>
            {renderBloodDrop('toughness-numbered', 100, 158)}
          </>
        )}
        {toughness >= 6 && (
          <text
            x="100"
            y="172"
            textAnchor="middle"
            fontFamily="Cinzel, Georgia, serif"
            fontSize="16"
            fontWeight="700"
            fill="#ffffff"
          >
            {toughness}
          </text>
        )}
      </g>

      {monster.name !== 'The Black Man' ? (
        <text
          x="156"
          y="148"
          textAnchor="middle"
          fontFamily="Cinzel, Georgia, serif"
          fontSize="24"
          fontWeight="700"
          fill="#ffffff"
          stroke="#232323"
          strokeWidth="3"
          paintOrder="stroke"
        >
          {combatRating}
        </text>
      ) : (
        <line
          data-testid="monster-token-horror-minus"
          x1="143"
          y1="170"
          x2="177"
          y2="170"
          stroke="#b9151b"
          strokeWidth="10"
          strokeLinecap="round"
        />
      )}

      {(combatDamage !== 0 || specialCombatDamage) && (
        <>
          <g data-testid="monster-token-combat-damage">
            <path
              d={MONSTER_COMBAT_DAMAGE_PATH}
              fill="#b9151b"
              stroke="#ffffff"
              strokeWidth="5"
            />
            <text
              x="156"
              y="175"
              textAnchor="middle"
              fontFamily="Cinzel, Georgia, serif"
              fontSize="18"
              fontWeight="700"
              fill="#ffffff"
            >
              {specialCombatDamage ? 'S' : combatDamage}
            </text>
          </g>
        </>
      )}
    </svg>
  );
};
